import { memo, useState } from 'react';
import FilterSlideoutSection from '../FilterSlideoutSection.jsx';
import FilterSlideoutOptionRow from '../FilterSlideoutOptionRow.jsx';
import { FILTER_SLIDEOUT_DISCLOSURE, FILTER_SLIDEOUT_OPTION_LIST } from '../filterSlideoutTokens.js';
import {
  clearSection,
  getSectionActiveCount,
  getVisibleItems,
  includesValue,
  selectedOptionsFirst,
  toggleArrayValue,
} from '../filterSlideout.utils.js';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Shared option-list renderer for multi, single, date presets, priority and toggles.
 */
const ChoiceListSection = ({
  section,
  control = 'checkbox',
  multiple = true,
  maxVisible = 4,
  sortSelectedFirst = false,
}) => {
  const activeCount = getSectionActiveCount(section);
  const [showAll, setShowAll] = useState(false);
  const options = sortSelectedFirst
    ? selectedOptionsFirst(section.options ?? [], section.value)
    : section.options ?? [];
  const { visibleItems, hiddenCount, hasOverflow } = getVisibleItems(
    options,
    section.maxVisible ?? maxVisible,
    showAll,
  );

  const handleToggle = (option) => {
    if (option.disabled || section.disabled) return;

    if (multiple) {
      section.onChange?.(toggleArrayValue(section.value, option.value));
      return;
    }

    const next =
      section.value === option.value && section.allValue !== undefined
        ? section.allValue
        : option.value;
    section.onChange?.(next);
  };

  return (
    <FilterSlideoutSection
      title={section.title}
      defaultExpanded={section.defaultExpanded ?? true}
      disabled={section.disabled}
      activeCount={activeCount}
      onClear={() => clearSection(section)}
    >
      <div className={FILTER_SLIDEOUT_OPTION_LIST}>
        {visibleItems.map((option) => (
          <FilterSlideoutOptionRow
            key={option.value}
            control={control}
            checked={includesValue(section.value, option.value)}
            disabled={section.disabled || option.disabled}
            label={option.label}
            description={option.description}
            color={option.color}
            onToggle={() => handleToggle(option)}
          />
        ))}

        {options.length === 0 ? (
          <p className="text-xs font-medium leading-5 text-content-muted">
            {section.emptyText ?? 'No options available'}
          </p>
        ) : null}

        {hasOverflow || showAll ? (
          <button
            type="button"
            className={FILTER_SLIDEOUT_DISCLOSURE}
            onClick={() => setShowAll((value) => !value)}
          >
            {showAll ? 'Show less' : `Show ${hiddenCount} more`}
            {showAll ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        ) : null}
      </div>
    </FilterSlideoutSection>
  );
};

export default memo(ChoiceListSection);
